import { G } from '@mobily/ts-belt';
import { useEffect, useMemo } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import { UseFormSetValue, UseFormTrigger } from 'react-hook-form';

import { GetB2bEmployeesOneResponse, GetB2bEmployeesQuery } from '@Common/types/bo/b2b/employees';
import { PostB2bWithdrawalBulkPositionsEndpoint } from '@Common/types/bo/b2b/withdrawals';
import { MAX_RANGE_ITEMS } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { PostBoExternalWithdrawalBulkPositionsEndpoint } from '@Common/types/bo_external/withdrawals';

import {
  BulkPositionItem,
  FormValues,
} from '@sh4res/bo-common/components/B2bWithdrawalBulkCreate/steps/utils';
import { useCreatePromise } from '@sh4res/bo-common/hooks/useCreatePromise';
import { ResourceBasePath } from '@sh4res/bo-common/hooks/useResourceBasePath';

type BulkPositionsRequestBody =
  | PostB2bWithdrawalBulkPositionsEndpoint['Client']['RequestBody']
  | PostBoExternalWithdrawalBulkPositionsEndpoint['Client']['RequestBody'];

type BulkPositionsResponseBody =
  | PostB2bWithdrawalBulkPositionsEndpoint['Client']['ResponseBody']
  | PostBoExternalWithdrawalBulkPositionsEndpoint['Client']['ResponseBody'];

type BulkRow = FormValues['bulk_rows'][number];
type EmployeeIdMap = Partial<Record<string, number[]>>;
type PositionMap = Partial<Record<number, BulkPositionItem>>;

type UseResolveBulkRowsParams = {
  rows: FormValues['bulk_rows'];
  currentPageStart: number;
  currentPageEnd: number;
  companyId: number | null;
  instrumentId: number | null;
  resourceBasePath: ResourceBasePath;
  setValue: UseFormSetValue<FormValues>;
  trigger: UseFormTrigger<FormValues>;
};

function buildEmployeeIdMap(
  employees: GetB2bEmployeesOneResponse[],
  getKey: (emp: GetB2bEmployeesOneResponse) => string | null | undefined,
): EmployeeIdMap {
  const map: EmployeeIdMap = {};
  for (const emp of employees) {
    const key = getKey(emp);
    if (G.isNotNullable(key)) {
      (map[key.trim()] ??= []).push(emp.id);
    }
  }
  return map;
}

function collectCandidateIds(
  rows: FormValues['bulk_rows'],
  pageStart: number,
  pageEnd: number,
  byNir: EmployeeIdMap,
  byNtt: EmployeeIdMap,
): Set<number> {
  const ids = new Set<number>();
  for (const row of rows.slice(pageStart, pageEnd)) {
    if (G.isNotNullable(row.employee_id)) {
      continue;
    }
    for (const id of byNir[row.nir.trim()] ?? byNtt[row.ntt.trim()] ?? []) {
      ids.add(id);
    }
  }
  return ids;
}

function resolveRow({
  row,
  index,
  pageStart,
  pageEnd,
  byNir,
  byNtt,
  positionMap,
}: {
  row: BulkRow;
  index: number;
  pageStart: number;
  pageEnd: number;
  byNir: EmployeeIdMap;
  byNtt: EmployeeIdMap;
  positionMap: PositionMap;
}): BulkRow {
  if (index < pageStart || index >= pageEnd || G.isNotNullable(row.employee_id)) {
    return row;
  }
  const candidates = byNir[row.nir.trim()] ?? byNtt[row.ntt.trim()] ?? [];
  if (candidates.length === 0) {
    return row;
  }
  const resolvedId = candidates.find((id) => G.isNotNullable(positionMap[id])) ?? candidates[0];
  const position = positionMap[resolvedId];
  return {
    ...row,
    employee_id: resolvedId,
    quantity_available: position?.quantity ?? 0,
    amount_available: G.isNotNullable(position) ? position.amount / 100 : 0,
    is_deceased: position?.is_deceased ?? false,
  };
}

export function useResolveBulkRows({
  rows,
  currentPageStart,
  currentPageEnd,
  companyId,
  instrumentId,
  resourceBasePath,
  setValue,
  trigger,
}: UseResolveBulkRowsParams): void {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [postBulkPositions] = useCreatePromise<
    BulkPositionsRequestBody,
    BulkPositionsResponseBody
  >();

  const unresolvedIdentifiers = useMemo(() => {
    const currentPageRows = rows.slice(currentPageStart, currentPageEnd);
    const unresolved = currentPageRows.filter((row) => G.isNullable(row.employee_id));

    const unresolvedNirs = unresolved.map((row) => row.nir.trim()).filter((nir) => nir.length > 0);

    const unresolvedNtts = unresolved
      .filter((row) => row.nir.trim().length === 0)
      .map((row) => row.ntt.trim())
      .filter((ntt) => ntt.length > 0);

    return {
      unresolvedNirs: [...new Set(unresolvedNirs)],
      unresolvedNtts: [...new Set(unresolvedNtts)],
    };
  }, [rows, currentPageStart, currentPageEnd]);

  const unresolvedIdentifiersKey = useMemo(
    () =>
      [
        [...unresolvedIdentifiers.unresolvedNirs].sort().join(','),
        [...unresolvedIdentifiers.unresolvedNtts].sort().join(','),
      ].join('|'),
    [unresolvedIdentifiers],
  );

  useEffect(() => {
    let isCancelled = false;

    const resolve = async () => {
      if (G.isNullable(companyId) || G.isNullable(instrumentId)) {
        return;
      }
      if (
        unresolvedIdentifiers.unresolvedNirs.length === 0 &&
        unresolvedIdentifiers.unresolvedNtts.length === 0
      ) {
        return;
      }

      try {
        // A NIR/NTT may match multiple employees (e.g. transferred between companies).
        const [employeesByNir, employeesByNtt] = await Promise.all([
          unresolvedIdentifiers.unresolvedNirs.length > 0
            ? dataProvider.getList<GetB2bEmployeesOneResponse>(`${resourceBasePath}employees`, {
                pagination: { page: 1, perPage: MAX_RANGE_ITEMS },
                sort: { field: 'id', order: 'ASC' },
                filter: {
                  company_id: companyId,
                  nir: unresolvedIdentifiers.unresolvedNirs,
                } satisfies GetB2bEmployeesQuery['filter'],
              })
            : Promise.resolve({ data: [] as GetB2bEmployeesOneResponse[] }),
          unresolvedIdentifiers.unresolvedNtts.length > 0
            ? dataProvider.getList<GetB2bEmployeesOneResponse>(`${resourceBasePath}employees`, {
                pagination: { page: 1, perPage: MAX_RANGE_ITEMS },
                sort: { field: 'id', order: 'ASC' },
                filter: {
                  company_id: companyId,
                  ntt: unresolvedIdentifiers.unresolvedNtts,
                } satisfies GetB2bEmployeesQuery['filter'],
              })
            : Promise.resolve({ data: [] as GetB2bEmployeesOneResponse[] }),
        ]);

        if (isCancelled) {
          return;
        }

        const byNir = buildEmployeeIdMap(employeesByNir.data, (emp) => emp.nir);
        const byNtt = buildEmployeeIdMap(employeesByNtt.data, (emp) => emp.ntt);

        const candidateIds = collectCandidateIds(
          rows,
          currentPageStart,
          currentPageEnd,
          byNir,
          byNtt,
        );
        if (candidateIds.size === 0) {
          return;
        }

        const positionsResponse = await postBulkPositions(
          `${resourceBasePath}withdrawals/bulk/positions`,
          {
            data: {
              b2b_company_id: companyId,
              instrument_id: instrumentId,
              employee_ids: [...candidateIds],
            },
          },
        );

        // eslint-disable-next-line disable-autofix/@typescript-eslint/no-unnecessary-condition -- the effect cleanup sets isCancelled after the await
        if (isCancelled) {
          return;
        }

        const positionMap: PositionMap = {};
        for (const pos of positionsResponse.positions) {
          positionMap[pos.employee_id] = pos;
        }

        const nextRows = rows.map((row, index) =>
          resolveRow({
            row,
            index,
            pageStart: currentPageStart,
            pageEnd: currentPageEnd,
            byNir,
            byNtt,
            positionMap,
          }),
        );

        const hasChanges = nextRows.some((row, idx) => row.employee_id !== rows[idx]?.employee_id);
        if (!hasChanges) {
          return;
        }

        setValue('bulk_rows', nextRows, { shouldDirty: true, shouldTouch: true });

        const fieldsToValidate: `bulk_rows.${number}.quantity_to_withdraw`[] = Array.from(
          { length: Math.min(currentPageEnd, nextRows.length) - currentPageStart },
          (_, idx): `bulk_rows.${number}.quantity_to_withdraw` =>
            `bulk_rows.${currentPageStart + idx}.quantity_to_withdraw`,
        );

        await trigger(fieldsToValidate);
      } catch {
        if (!isCancelled) {
          notify('resources.bo_common.b2b.withdrawals.bulk_create.errors.load_positions_failed', {
            type: 'warning',
          });
        }
      }
    };

    void resolve();
    return () => {
      isCancelled = true;
    };
  }, [
    companyId,
    instrumentId,
    currentPageEnd,
    currentPageStart,
    dataProvider,
    notify,
    postBulkPositions,
    resourceBasePath,
    rows,
    setValue,
    trigger,
    unresolvedIdentifiers,
    unresolvedIdentifiersKey,
  ]);
}
